import {
  AppShell,
  Box,
  Burger,
  Drawer,
  Group,
  Text,
  Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import {
  createContext,
  Suspense,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Outlet } from "react-router-dom";

import { restoreFocus } from "../../app/a11y.js";
import { PrimaryNavigation } from "../admin/AdminNavigation.js";

const CUSTOMER_DESKTOP_QUERY = "(min-width: 48em)";

const CustomerHeaderActionTargetContext = createContext<HTMLElement | null>(null);

export function CustomerHeaderActionPortal({ children }: { readonly children: ReactNode }) {
  const target = useContext(CustomerHeaderActionTargetContext);
  return target === null ? null : createPortal(children, target);
}

export interface CustomerShellProps {
  readonly hasReportAccess: boolean;
  readonly role: "admin" | "super_admin" | "viewer";
}

export function CustomerShell({
  hasReportAccess,
  role,
}: CustomerShellProps) {
  const [headerActionTarget, setHeaderActionTarget] = useState<HTMLDivElement | null>(null);
  const [navigationOpened, setNavigationOpened] = useState(false);
  const desktopNavigationRef = useRef<HTMLDivElement>(null);
  const navigationTriggerRef = useRef<HTMLButtonElement>(null);
  const navigationFocusRef = useRef<"desktop" | "mobile" | null>(null);
  const isDesktop = useMediaQuery(
    CUSTOMER_DESKTOP_QUERY,
    undefined,
    { getInitialValueInEffect: false },
  );
  const wasDesktopRef = useRef(isDesktop);
  const closeNavigation = () => setNavigationOpened(false);

  useEffect(() => {
    const trackNavigationFocus = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (desktopNavigationRef.current?.contains(target)) {
        navigationFocusRef.current = "desktop";
        return;
      }
      const mobileDrawer = document.getElementById("customer-navigation-drawer");
      navigationFocusRef.current = navigationTriggerRef.current?.contains(target)
        || mobileDrawer?.contains(target)
        ? "mobile"
        : null;
    };
    document.addEventListener("focusin", trackNavigationFocus);
    return () => document.removeEventListener("focusin", trackNavigationFocus);
  }, []);

  useEffect(() => {
    const wasDesktop = wasDesktopRef.current;
    wasDesktopRef.current = isDesktop;
    if (wasDesktop === isDesktop) return;

    if (!isDesktop && navigationFocusRef.current === "desktop") {
      restoreFocus(navigationTriggerRef.current, null);
      return;
    }

    if (isDesktop && (navigationOpened || navigationFocusRef.current === "mobile")) {
      setNavigationOpened(false);
      restoreFocus(
        desktopNavigationRef.current?.querySelector<HTMLElement>("[aria-current='page']") ?? null,
        null,
      );
    }
  }, [isDesktop, navigationOpened]);

  return (
    <CustomerHeaderActionTargetContext.Provider value={headerActionTarget}>
      <AppShell
        className="customer-shell"
        data-testid="customer-shell"
        header={{ height: { base: 52, sm: 58 } }}
        padding={0}
        withBorder={false}
      >
        <AppShell.Header className="customer-header">
          <Group className="customer-header__inner" gap="lg" h="100%" wrap="nowrap">
            <Title className="customer-header__title" order={1}>Video analytics</Title>
            {isDesktop ? (
              <Box
                className="customer-header__desktop-navigation"
                ref={desktopNavigationRef}
              >
                <PrimaryNavigation hasReportAccess={hasReportAccess} role={role} />
              </Box>
            ) : null}
            <Group className="customer-header__actions" gap="xs" ml="auto" wrap="nowrap">
              <div
                className="customer-header__route-actions"
                ref={setHeaderActionTarget}
              />
              {isDesktop ? null : (
                <Burger
                  aria-controls="customer-navigation-drawer"
                  aria-expanded={navigationOpened}
                  aria-label={navigationOpened ? "Close navigation" : "Open navigation"}
                  className="customer-header__navigation-trigger"
                  onClick={() => setNavigationOpened((opened) => !opened)}
                  opened={navigationOpened}
                  ref={navigationTriggerRef}
                  size="sm"
                />
              )}
            </Group>
          </Group>
        </AppShell.Header>

        <AppShell.Main className="customer-shell__main">
          <Suspense fallback={<Text role="status">Loading workspace…</Text>}>
            <Outlet />
          </Suspense>
        </AppShell.Main>

        {isDesktop ? null : (
          <Drawer
            closeButtonProps={{ "aria-label": "Close navigation" }}
            id="customer-navigation-drawer"
            onClose={closeNavigation}
            onExitTransitionEnd={() => restoreFocus(navigationTriggerRef.current, null)}
            opened={navigationOpened}
            position="right"
            returnFocus={false}
            size="min(20rem, 90vw)"
            title="Navigation"
            trapFocus
          >
            <PrimaryNavigation
              hasReportAccess={hasReportAccess}
              onNavigate={closeNavigation}
              orientation="vertical"
              role={role}
            />
          </Drawer>
        )}
      </AppShell>
    </CustomerHeaderActionTargetContext.Provider>
  );
}
